import { useState, useEffect } from 'react';
import AppLayout from '@cloudscape-design/components/app-layout';
import TopNavigation from '@cloudscape-design/components/top-navigation';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Input from '@cloudscape-design/components/input';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import SideNavigation from '@cloudscape-design/components/side-navigation';
import AuthModal from './AuthModal';
import { getCurrentUser, signOut, AuthUser } from './auth';
import { fetchAllTenants, onboardTenant, Tenant } from './agentcore';
import AdminDashboard from './AdminDashboard';

const ADMIN_EMAIL = 'admin.dev@anacodicai.com';

function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Onboarding state
  const [tenantId, setTenantId] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [placeId, setPlaceId] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [onboarding, setOnboarding] = useState(false);
  const [onboardMessage, setOnboardMessage] = useState('');
  const [onboardError, setOnboardError] = useState('');

  // Tenants list state
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<string>('');

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      loadTenants();
    }
  }, [user]);

  const checkAuth = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (err) {
      setUser(null);
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleSignOut = () => {
    signOut();
    setUser(null);
  };

  const handleAuthSuccess = async () => {
    setShowAuthModal(false);
    await checkAuth();
  };

  const loadTenants = async () => {
    try {
      setLoadingTenants(true);
      console.log('🔍 Loading tenants for user:', user?.email);
      console.log('🔍 User object:', user);
      
      // Decode JWT to see what email is being sent
      try {
        const token = await (await import('./auth')).getAccessToken();
        if (token) {
          const parts = token.split('.');
          if (parts.length >= 2) {
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            console.log('🔍 JWT payload email:', payload.email || payload['cognito:username']);
            console.log('🔍 JWT payload:', payload);
          }
        }
      } catch (e) {
        console.warn('Could not decode JWT:', e);
      }
      
      const response = await fetchAllTenants();
      console.log('📋 Tenants response:', JSON.stringify(response, null, 2));
      // Handle empty or missing tenants array
      const tenantsList = response?.tenants || [];
      console.log('📋 Setting tenants list:', tenantsList);
      console.log('📋 Tenants count:', tenantsList.length);
      
      if (tenantsList.length === 0) {
        console.warn('⚠️ No tenants found in response. This could mean:');
        console.warn('  1. Email mismatch between onboarding and listing');
        console.warn('  2. DynamoDB eventual consistency (wait longer)');
        console.warn('  3. authenticated_user is None in backend');
      }
      
      setTenants(tenantsList);
      if (tenantsList.length > 0 && !selectedTenant) {
        setSelectedTenant(tenantsList[0].tenant_id);
      }
    } catch (err: any) {
      console.error('❌ Failed to load tenants:', err);
      console.error('❌ Error details:', {
        message: err.message,
        stack: err.stack,
        response: err.response
      });
      // Set empty array on error to prevent crashes
      setTenants([]);
    } finally {
      setLoadingTenants(false);
    }
  };

  // Check if current user is admin
  const isAdmin = user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();

  const handleAddTenant = async () => {
    if (!tenantId.trim() || !businessId.trim() || !placeId.trim() || !customerEmail.trim()) {
      setOnboardError('Please enter Tenant ID, Business ID, Place ID, and Customer Email');
      return;
    }

    try {
      setOnboarding(true);
      setOnboardError('');
      setOnboardMessage(`Adding ${tenantId}_${businessId} for ${customerEmail}...`);

      const result = await onboardTenant(tenantId, businessId, placeId, customerEmail);

      if (result.success) {
        setOnboardMessage(`✓ Successfully added ${tenantId}_${businessId} for ${customerEmail}!`);
        setTenantId('');
        setBusinessId('');
        setPlaceId('');
        setCustomerEmail('');
        
        // Decode JWT to see what email was used for onboarding
        try {
          const token = await (await import('./auth')).getAccessToken();
          if (token) {
            const parts = token.split('.');
            if (parts.length >= 2) {
              const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
              console.log('✅ Onboarding successful with email:', payload.email || payload['cognito:username']);
            }
          }
        } catch (e) {
          console.warn('Could not decode JWT:', e);
        }
        
        // Wait longer for DynamoDB eventual consistency (increased to 5 seconds)
        console.log('⏳ Onboarding successful, waiting 5 seconds for DynamoDB consistency...');
        await new Promise(resolve => setTimeout(resolve, 5000));
        console.log('🔄 Reloading tenants list...');
        await loadTenants();
        console.log('✅ Tenants reloaded');
        // Clear success message after 3 seconds
        setTimeout(() => setOnboardMessage(''), 3000);
      } else {
        setOnboardError(result.message);
      }
    } catch (err: any) {
      console.error('Onboarding error details:', err);
      const errorMessage = err.message || err.error || 'Failed to onboard tenant';
      setOnboardError(errorMessage);
      console.error('Full error object:', JSON.stringify(err, null, 2));
    } finally {
      setOnboarding(false);
    }
  };



  if (checkingAuth) {
    return (
      <div style={{ padding: '20px', fontFamily: 'sans-serif' }}>
        <h1>Business Specialist Admin Dashboard</h1>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <AuthModal
        visible={showAuthModal}
        onDismiss={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
      <TopNavigation
        identity={{
          href: "#",
          title: "Business Specialist Admin Dashboard"
        }}
        utilities={[
          {
            type: "button",
            text: user ? `${user.email} | Sign Out` : "Sign In",
            iconName: user ? "user-profile" : "lock-private",
            onClick: () => {
              if (user) {
                handleSignOut();
              } else {
                setShowAuthModal(true);
              }
            }
          }
        ]}
        i18nStrings={{
          overflowMenuTriggerText: "More",
          overflowMenuTitleText: "All"
        }}
      />
      <AppLayout
        navigation={
          user ? (
            <SideNavigation
              header={{
                text: 'Tenants',
                href: '#'
              }}
              items={
                loadingTenants
                  ? [{ type: 'link', text: 'Loading...', href: '#' }]
                  : tenants.length === 0
                  ? [{ type: 'link', text: 'No tenants yet', href: '#' }]
                  : tenants.map(tenant => ({
                      type: 'link' as const,
                      text: tenant.tenant_id,
                      href: `#${tenant.tenant_id}`,
                      info: tenant.business_type
                    }))
              }
              activeHref={`#${selectedTenant}`}
              onFollow={event => {
                event.preventDefault();
                const href = event.detail.href;
                const tenantId = href.substring(1); // Remove '#'
                setSelectedTenant(tenantId);
              }}
            />
          ) : undefined
        }
        navigationHide={!user}
        toolsHide={true}
        disableContentPaddings={false}
        contentType="default"
        content={
          user && !isAdmin ? (
            <ContentLayout defaultPadding>
              <Box textAlign="center" padding="xxl">
                <Box variant="h2" margin={{ bottom: 'm' }}>
                  Access Denied
                </Box>
                <Box variant="p" color="text-body-secondary">
                  This admin dashboard is restricted to authorized administrators only.
                </Box>
                <Box variant="p" color="text-body-secondary" margin={{ top: 's' }}>
                  Signed in as: <strong>{user.email}</strong>
                </Box>
                <Box margin={{ top: 'm' }}>
                  <Button onClick={handleSignOut}>Sign Out</Button>
                </Box>
              </Box>
            </ContentLayout>
          ) : user && isAdmin ? (
            <ContentLayout
              header={
                <Header variant="h1" description="Manage and monitor all client tenants">
                  Business Specialist Dashboard
                </Header>
              }
            >
              <SpaceBetween size="l">
                {/* Onboarding Section */}
                <Container
                  header={
                    <Header variant="h2" description="Add a new tenant to the system">
                      Onboard New Tenant
                    </Header>
                  }
                >
                  <SpaceBetween size="m">
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '150px' }}>
                        <Box variant="awsui-key-label" margin={{ bottom: 'xxxs' }}>
                          Tenant ID
                        </Box>
                        <Input
                          value={tenantId}
                          onChange={({ detail }) => setTenantId(detail.value)}
                          placeholder="e.g., tenant1"
                          disabled={onboarding}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '150px' }}>
                        <Box variant="awsui-key-label" margin={{ bottom: 'xxxs' }}>
                          Business ID
                        </Box>
                        <Input
                          value={businessId}
                          onChange={({ detail }) => setBusinessId(detail.value)}
                          placeholder="e.g., business1"
                          disabled={onboarding}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <Box variant="awsui-key-label" margin={{ bottom: 'xxxs' }}>
                          Place ID
                        </Box>
                        <Input
                          value={placeId}
                          onChange={({ detail }) => setPlaceId(detail.value)}
                          placeholder="e.g., ChIJicTrthzXujsR0bU_hXpu-bI"
                          disabled={onboarding}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <Box variant="awsui-key-label" margin={{ bottom: 'xxxs' }}>
                          Customer Email
                        </Box>
                        <Input
                          value={customerEmail}
                          onChange={({ detail }) => setCustomerEmail(detail.value)}
                          placeholder="e.g., customer@example.com"
                          disabled={onboarding}
                        />
                      </div>
                      <Button
                        variant="primary"
                        onClick={handleAddTenant}
                        loading={onboarding}
                        disabled={onboarding}
                      >
                        {onboarding ? 'Adding...' : 'Add Tenant'}
                      </Button>
                    </div>

                    {onboardMessage && (
                      <Alert type="success" dismissible onDismiss={() => setOnboardMessage('')}>
                        {onboardMessage}
                      </Alert>
                    )}

                    {onboardError && (
                      <Alert type="error" dismissible onDismiss={() => setOnboardError('')}>
                        {onboardError}
                      </Alert>
                    )}
                  </SpaceBetween>
                </Container>

                {/* Dashboard Section */}
                {selectedTenant ? (
                  <AdminDashboard userEmail={user.email} tenantId={selectedTenant} />
                ) : (
                  <Box textAlign="center" padding="xxl">
                    <Box variant="p" color="text-body-secondary">
                      {tenants.length === 0
                        ? 'No tenants yet. Add your first tenant above.'
                        : 'Select a tenant from the sidebar to view dashboard.'}
                    </Box>
                  </Box>
                )}
              </SpaceBetween>
            </ContentLayout>
          ) : (
            <ContentLayout defaultPadding>
              <Box textAlign="center" padding="xxl">
                <Box variant="h2" margin={{ bottom: 'm' }}>
                  Business Specialist Dashboard
                </Box>
                <Box variant="p" color="text-body-secondary">
                  Please sign in to access the admin dashboard
                </Box>
              </Box>
            </ContentLayout>
          )
        }
      />
    </>
  );
}

export default App;
